// src/components/Bookkauslomake/Bookkauslomake.jsx
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import emailjs from "emailjs-com";
import { toast } from "react-toastify";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

import StarRating from "../StarRating/StarRating";
import ResponsiveButton from "../ResponsiveButton/NeonButton.jsx";
import RecaptchaField from "../RecaptchaField/RecaptchaField";
import { GMAPS_LOADER_OPTIONS } from "../../components/utils/gmapsLoader";

// Helsinki-”tänään” muodossa YYYY-MM-DD (string-vertailu toimii)
const todayYmdHEL = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Helsinki", year: "numeric", month: "2-digit", day: "2-digit"
  }).format(new Date());


/* ===================== YMPÄRISTÖASETUKSET ===================== */
const BASE =
  process.env.REACT_APP_API_BASE ||
  `${window.location.protocol}//${window.location.hostname}:8080`;

const API_BASE_ABS =
  process.env.REACT_APP_API_BASE_ABS || "https://api.buukkeri.fi";

/* EmailJS */

/* Holdin TTL frontille fallbackiksi (palvelin on totuus, tämä vain varalle) */
const HOLD_TTL_HOURS = Number(process.env.REACT_APP_HOLD_TTL_HOURS ?? 1);

const EMAILJS_USER_ID = process.env.REACT_APP_EMAILJS_USER_ID;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID_PERFORMER =
process.env.REACT_APP_EMAILJS_TEMPLATE_ID_PERFORMER || "";
const EMAILJS_TEMPLATE_ID_CUSTOMER_PERFORMER =
process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CUSTOMER_PERFORMER || "";
// 👇 UUSI: erillinen templaatti palautteelle (fallbackina käytetään viestitemplaattia)
const EMAILJS_TEMPLATE_ID_PERFORMER_FEEDBACK =
process.env.REACT_APP_EMAILJS_TEMPLATE_ID_PERFORMER_FEEDBACK || "";

/* Dev-ohitukset */
const BYPASS_RECAPTCHA =
  String(process.env.REACT_APP_BYPASS_RECAPTCHA || "").toLowerCase() === "true";
const MAPS_DEV_FAKE =
  String(process.env.REACT_APP_MAPS_DEV_FAKE || "").toLowerCase() === "true";
const BYPASS_EMAILJS =
  String(process.env.REACT_APP_BYPASS_EMAILJS || "").toLowerCase() === "true";

/* ===================== APUFUNKTIOT ===================== */

async function safeEmailSend(serviceId, templateId, params) {
  if (BYPASS_EMAILJS) {
    console.warn("[EMAILJS] BYPASSED (REACT_APP_BYPASS_EMAILJS=true)");
    return { bypassed: true };
  }
  if (!EMAILJS_USER_ID || !serviceId || !templateId) {
    throw new Error("EmailJS .env puuttuu: USER_ID / SERVICE_ID / TEMPLATE_ID");
  }

  // Siivoa parametrit: säilytä booleanit booleaneina, muut stringeiksi
  const clean = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v == null) return;
    clean[k] = (typeof v === "boolean") ? v : String(v);
  });

  try { emailjs.init(String(EMAILJS_USER_ID).trim()); }
  catch (e) { console.warn("[EMAILJS] init error", e); }

  // 🔍 Selkeä debug: tärkeimmät kentät + koko payload
  const pick = (o, keys) => Object.fromEntries(keys.map(k => [k, o[k]]));
  const important = pick(clean, [
    "to_email","user_email","reply_to",
    "to_email","email","user_email","reply_to",
    "is_band","is_company","band_name","target_name",
    "event_date","event_location","gig_length","region",
    "base_price","surcharge","total_price",
    "base_price_num","surcharge_num","total_price_num"
  ]);
  console.groupCollapsed(`[EMAILJS] payload → ${String(templateId).trim()}`);
  try { console.table(important); } catch { console.log(important); }
  console.log("FULL PARAMS →", clean);
  console.groupEnd();

  try {
    const res = await emailjs.send(
      String(serviceId).trim(),
      String(templateId).trim(),
      clean,
      String(EMAILJS_USER_ID).trim()
    );
    console.log("[EMAILJS] send OK →", {
      serviceId: String(serviceId).trim(),
      templateId: String(templateId).trim(),
      to: clean.to_email || clean.email || clean.user_email || "(param 'to_email' / 'email' puuttuu)",
    });
    return res;
  } catch (err) {
    console.error("[EMAILJS] send FAILED →", {
      serviceId: String(serviceId).trim(),
      templateId: String(templateId).trim(),
      to: clean.to_email || clean.email || clean.user_email || "(param 'to_email' / 'email' puuttuu)",
      err
    });
    throw err;
  }
}

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const capitalizeEachWord = (text) =>
  String(text || "")
    .split(/[\s-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("-");

const COMMISSION_RULES = {
  dj: 0.15,
  bilebandi: 0.15,   // bile-/coverbändi
  band: 0.15,        // fallback "bändi"
  trubaduuri: 0.12,
  duo: 0.15,
  sooloartisti: 0.15, // 👈 UUSI
  default: 0.15
};


function categorizeArtist(bandLike) {
  const t = norm(bandLike?.artist_type || "");

  // Trubaduuri
  if (/trub/.test(t)) return "trubaduuri";

  // DJ
  if (/^dj$|deejay/.test(t)) return "dj";

  // Duo (esim. "Akustinen Duo", "Hääduo", tms.)
  if (/duo/.test(t)) return "duo";

  // Sooloartisti (esim. "Sooloartisti", "Sooloartisti (coverit)")
  if (/soolo|solo/.test(t)) return "sooloartisti";

  // Bile-/coverbändit yms.
  if (/bile|cover|band|bndi/.test(t)) return "bilebandi";

  // Fallback: tavallinen bändi
  return "band";
}

function commissionPctFor(bandLike) {
  const cat = categorizeArtist(bandLike);
  return COMMISSION_RULES[cat] ?? COMMISSION_RULES.default;
}

// FI/SV/EN → kanoninen maakunta
const REGION_CANON = {
  uusimaa: "Uusimaa",
  nyland: "Uusimaa",
  varsinaissuomi: "Varsinais-Suomi",
  southwestfinland: "Varsinais-Suomi",
  egentligenfinland: "Varsinais-Suomi",
  lappi: "Lappi",
  lapland: "Lappi",
  lappland: "Lappi",
  pirkanmaa: "Pirkanmaa",
  satakunta: "Satakunta",
  kantahame: "Kanta-Häme",
  paijathame: "Päijät-Häme",
  kymenlaakso: "Kymenlaakso",
  kainuu: "Kainuu",
  pohjanmaa: "Pohjanmaa",
  ostrobothnia: "Pohjanmaa",
  etelapohjanmaa: "Etelä-Pohjanmaa",
  keskipohjanmaa: "Keski-Pohjanmaa",
  pohjoispohjanmaa: "Pohjois-Pohjanmaa",
  keskisuomi: "Keski-Suomi",
  etelasavo: "Etelä-Savo",
  pohjoissavo: "Pohjois-Savo",
  etelakarjala: "Etelä-Karjala",
  pohjoiskarjala: "Pohjois-Karjala",
  ahvenanmaa: "Ahvenanmaa",
  aland: "Ahvenanmaa",
};
const toCanon = (r) => REGION_CANON[norm(r)] || capitalizeEachWord(r);

// Matka-/aika Googlelta (bändeille; yrityksille ei käytetä)
const getDistanceAndDuration = (origin, destination) =>
  new Promise((resolve, reject) => {
    const noGoogle = typeof window === "undefined" || !window.google;
    if (MAPS_DEV_FAKE || noGoogle) {
      return resolve({
        distance: 0,
        duration: 0,
        distanceText: "0 km",
        durationText: "0 min",
      });
    }
    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === "OK") {
          const el = response.rows[0].elements[0];
          if (el.status === "OK") {
            resolve({
              distance: el.distance.value / 1000,
              duration: el.duration.value / 3600,
              distanceText: el.distance.text,
              durationText: el.duration.text,
            });
          } else reject(new Error("Matkan laskenta epäonnistui: " + el.status));
        } else reject(new Error("Google API status: " + status));
      }
    );
  });

/* --------- AUKIOLOAJAT: tuetaan object- ja array-malleja --------- */
const WEEK_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
function parseOpeningHours(src) {
  if (!src) return null;
  const out = {};
  if (Array.isArray(src) && src.length === 7) {
    src.forEach((it, idx) => {
      out[WEEK_KEYS[idx]] = {
        open: it?.open || "",
        close: it?.close || "",
      };
    });
    return out;
  }
  // oletetaan olio jossa avaimet (suomi/englanti käy)
  const mapKey = (k) => {
    const s = String(k || "").toLowerCase();
    if (/^su/.test(s) || s === "sun" || s === "sunday") return "sun";
    if (/^ma/.test(s) || s === "mon" || s === "monday") return "mon";
    if (/^ti/.test(s) || s === "tue" || s === "tuesday") return "tue";
    if (/^ke/.test(s) || s === "wed" || s === "wednesday") return "wed";
    if (/^to/.test(s) || s === "thu" || s === "thursday") return "thu";
    if (/^pe/.test(s) || s === "fri" || s === "friday") return "fri";
    if (/^la/.test(s) || s === "sat" || s === "saturday") return "sat";
    return null;
  };
  Object.entries(src || {}).forEach(([k, v]) => {
    const key = mapKey(k);
    if (!key) return;
    out[key] = { open: v?.open || "", close: v?.close || "" };
  });
  return Object.keys(out).length ? out : null;
}

// --- Tarjous-cooldown (FE-esto) ---
// Tallennus localStorageen; BE on totuus, tämä vain FE-estoa varten.
const COOLDOWN_KEY = "bilebox:quote-cooldown";

function loadCooldown() {
  try { return JSON.parse(localStorage.getItem(COOLDOWN_KEY) || "null"); }
  catch { return null; }
}
function saveCooldown(obj) {
  try { localStorage.setItem(COOLDOWN_KEY, JSON.stringify(obj)); } catch {}
}
function clearCooldown() {
  try { localStorage.removeItem(COOLDOWN_KEY); } catch {}
}


/* ===================== KOMPONENTTI ===================== */
export default function Bookkauslomake({
  bandSlug,            // legacy: bändille
  companySlug,         // uusi: yritykselle
  targetType,          // 'band' | 'company' (autodetect, jos jätetään pois)
  selectedDate,        // { iso, formatted }
  defaultMode = "quote",
  quoteDisabled = false,
  hideQuoteOption = false,   // 👈 UUSI
  prefill = null,            // 👈 UUSI: { email, date, pin, feedback }
  hideInlineBanner = false,        // 👈 UUSI: piilota lomakkeen oma banneri
  hideSuccessCard = false,   // 👈 UUSI
  onSubmitSuccess,                 // 👈 UUSI: ilmoita parentille onnistumisesta
  previewStatic = false,
}) {
  const kind = useMemo(() => {
    if (targetType) return targetType;
    if (companySlug) return "company";
    return "band";
  }, [targetType, companySlug]);

  const slug = kind === "company" ? companySlug : bandSlug;

  const [mode, setMode] = useState(defaultMode);

  // Johdettu: puhdistettu prefill.pin (vain numerot); memoidaan, jotta efektien riippuvuudet pysyvät vakaina
  const prefillPin = useMemo(
    () => (prefill?.pin || "").replace(/\D/g, ""),
    [prefill?.pin]
  );

  // --- Johdetut arvot ylös: käytössä efekteissäkin (korjaa no-use-before-define) ---
  const eventDateIso = useMemo(
    () => (selectedDate?.iso || prefill?.date || "").slice(0, 10),
    [selectedDate?.iso, prefill?.date]
  );
  const isPastSelected = useMemo(() => {
    const ymd = (eventDateIso || "").slice(0, 10);
    if (!ymd) return false;
    return ymd < todayYmdHEL();
  }, [eventDateIso]);
  // Banneri valitun päivän statuksesta
  const statusLower = String(selectedDate?.status || "").toLowerCase();
  const dateLabelFi = eventDateIso
    ? new Date(eventDateIso + "T12:00:00").toLocaleDateString("fi-FI")
    : "";
  const banner = useMemo(() => {
    if (!eventDateIso) return null;
    if (isPastSelected) return { type: "error", text: `Päivä on menneisyydessä (${dateLabelFi}) – tarjousta ei voi pyytää.` };
    if (statusLower === "booked")  return { type: "error", text: `Päivä on varattu (${dateLabelFi}).` };
    if (statusLower === "pending") return { type: "warn",  text: `Tarjous odottaa hyväksyntää (${dateLabelFi}) – ei buukattavissa.` };
    if (statusLower === "open")    return { type: "ok",    text: `✓ Esiintyjä on vapaana ajankohtana ${dateLabelFi} – voit kysyä tarjousta!` };
    return { type: "warn", text: "Päivä ei ole buukattavissa." };
  }, [eventDateIso, isPastSelected, statusLower, dateLabelFi]);

useEffect(() => {
  const isOpenDay = selectedDate?.status === "open";
  if (isOpenDay && !isPastSelected && !quoteDisabled && !hideQuoteOption) {
    setMode("quote");
  } else if (mode === "quote" && (isPastSelected || selectedDate?.status !== "open")) {
    // mennyt / ei oikeasti buukattavissa -> pois quote-tilasta
    setMode("feedback"); // tai "message" – kumman haluat
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedDate?.iso, selectedDate?.status, isPastSelected, quoteDisabled, hideQuoteOption]);


// Scrollaa lomakkeelle mobiilissa, oli päivä open/pending/booked TAI ei buukattavissa
useEffect(() => {
  if (!selectedDate?.iso) return;

  const s = String(selectedDate?.status || "").toLowerCase();

  // Tulkitse kaikki kalenteriklipit “kelpoisiksi” scrollata
  const isCalendarClick =
    s === "" ||
    ["open", "pending", "booked", "blackout", "closed", "unavailable", "busy", "not_bookable", "disabled"]
      .includes(s);

  if (!isCalendarClick) return;

  // vain mobiilissa
  const isMobile =
    (typeof window !== "undefined" &&
      window.matchMedia?.("(max-width: 768px)").matches) ||
    (typeof navigator !== "undefined" && (navigator.maxTouchPoints > 0));

  if (!isMobile) return;

  const t = setTimeout(() => {
    const anchor =
      document.querySelector("[data-contact-form]") ||
      document.getElementById("contact-form");

    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // fokus: open → tapahtumapaikka, muut → viestikenttä
    const focusEl =
      s === "open"
        ? document.getElementById("event_location")
        : document.querySelector('textarea[placeholder*="viest"]') ||
          document.querySelector("textarea");
    try { focusEl?.focus(); } catch {}
  }, 0);

  return () => clearTimeout(t);
}, [selectedDate?.iso, selectedDate?.status, quoteDisabled, hideQuoteOption]);

  // Kohteen data (bändi TAI yritys)
  const [targetData, setTargetData] = useState(null);

  // Bändin setit; yrityksellä tyhjä
  const [availableSets, setAvailableSets] = useState([]);

    // Näytettävät setit alueen mukaan
  const [allBandSets, setAllBandSets] = useState([]);
  const [setsPerRegion, setSetsPerRegion] = useState({}); // { "Uusimaa": ["2x45min", ...] }

  // Kohteen sallitut alueet (string-nimet)
  const [allowedRegions, setAllowedRegions] = useState([]);

  // Yrityksen hinnastodata
  const [companyPricing, setCompanyPricing] = useState([]); // [{region_id, price?, hourly_rate?}]
  const [regionIdToName, setRegionIdToName] = useState({}); // { id: "Uusimaa" }

  // Hinnoittelumoodi & tunnit (yritykselle)
  const [billingMode, setBillingMode] = useState("flat");  // 'flat' | 'hourly'
  const [minHours, setMinHours] = useState(1);
  const [hourlyRate, setHourlyRate] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  // Muista: lisää samaan lohkoon kuin muutkin useState:t
const [lastSubmitMode, setLastSubmitMode] = useState(null); 
const [lastSubmitMeta, setLastSubmitMeta] = useState({
  email: "",
  ttlLabel: "",
  holdExpiresFi: ""
});

  // Aukioloajat (yritykselle)
  const [openingHours, setOpeningHours] = useState(null);
  const [feedbackPin, setFeedbackPin] = useState("");
  // PIN-tila: idle|checking|ok|invalid|not_active|expired|not_found|error
  const [pinStatus, setPinStatus] = useState("idle");
  const pinOk = pinStatus === "ok";

  // Prefill + autovalidointi: tehdään vain kerran per mount
  const prefillAppliedRef = useRef(false);

  const targetName =
    targetData?.name || targetData?.band_name || slug || "–";
  const targetTypeLabel = kind === "company" ? "Yritys" : (targetData?.artist_type || "Esiintyjä");

  // Yhteiset kentät
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    message: "",
    gig_length: "", // vain bändeille
  });

  const { isLoaded, loadError } = useJsApiLoader(GMAPS_LOADER_OPTIONS);
  const hasMapsKey = !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const enableMaps = hasMapsKey && !MAPS_DEV_FAKE;
  const mapsReady = enableMaps && isLoaded && !loadError; // Google on oikeasti käytössä
  const autocompleteRef = useRef(null);
  const [eventPlace, setEventPlace] = useState("");
  const [placeSelected, setPlaceSelected] = useState(false);
  const [locError, setLocError] = useState("");
  const [priceRegion, setPriceRegion] = useState(""); // kanoninen nimi
  const [priceForRegion, setPriceForRegion] = useState(null); // yrityksen könttähinta

    const [submitting, setSubmitting] = useState(false);

  // Palaute
  const [stars, setStars] = useState(0);
  const [collapsed, setCollapsed] = useState(false);

  // reCAPTCHA
  const recaptchaRef = useRef(null);
  const pinInputRef = useRef(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  // Tarjous-cooldown
  const [cooldownUntil, setCooldownUntil] = useState(null); // ms-epoch
  const [cooldownLeft, setCooldownLeft]   = useState(0);    // sekunteina

  // Lataa cooldown
useEffect(() => {
  const c = loadCooldown();
  if (c?.untilIso) {
    const until = new Date(c.untilIso).getTime();
    if (Number.isFinite(until) && until > Date.now()) {
      setCooldownUntil(until);
      setCooldownLeft(Math.max(0, Math.floor((until - Date.now()) / 1000)));
    } else {
      clearCooldown();
    }
  }
}, []);

// --- Päivitä bändin setit valitun maakunnan mukaan ---
useEffect(() => {
  if (kind !== "band") return;

  // Kun alue ei vielä ole tiedossa → näytä kaikki bändin setit
  if (!priceRegion) {
    setAvailableSets(allBandSets);
    setForm(f => ({
      ...f,
      gig_length: allBandSets.includes(f.gig_length) ? f.gig_length : ""
    }));
    return;
  }

  // Haetaan aluekohtainen sallittu settilista; jos ei nimettyjä rivejä → kaikki setit
  const allowed = (setsPerRegion[priceRegion] && setsPerRegion[priceRegion].length)
    ? setsPerRegion[priceRegion]
    : allBandSets;

  setAvailableSets(allowed);

  // Jos käyttäjä oli valinnut setin, jota ei tällä alueella ole hinnoiteltu → nollaa valinta
  setForm(f => ({
    ...f,
    gig_length: allowed.includes(f.gig_length) ? f.gig_length : ""
  }));
}, [kind, priceRegion, allBandSets, setsPerRegion]);


// Tikkaa sekunnin välein
useEffect(() => {
  if (!cooldownUntil) return;
  const tick = () => {
    const left = Math.floor((cooldownUntil - Date.now()) / 1000);
    setCooldownLeft(left);
    if (left <= 0) {
      setCooldownUntil(null);
      clearCooldown();
    }
  };
  tick();
  const id = setInterval(tick, 1000);
  return () => clearInterval(id);
}, [cooldownUntil]);

// --- KESTÄVÄ SUBMIT: offline-tila + pre-click -latch (kilpajuoksun esto) ---
const [offline, setOffline] = useState(
  typeof navigator !== "undefined" ? !navigator.onLine : false
);

useEffect(() => {
  const on = () => setOffline(false);
  const off = () => setOffline(true);
  window.addEventListener("online", on);
  window.addEventListener("offline", off);
  return () => {
    window.removeEventListener("online", on);
    window.removeEventListener("offline", off);
  };
}, []);

// Kilpajuoksun esto: jos käyttäjä ehtii klikata ennen kuin React asettaa disabledin
const preClickLatch = useRef(false);

// Kun kalenterissa sama päivä ei ole enää pending → poista FE-cooldown
useEffect(() => {
  const c = loadCooldown();
  const heldDate = c?.date;                       // "yyyy-mm-dd"
  const selDate = (selectedDate?.iso || "").slice(0, 10);
  const status  = String(selectedDate?.status || "").toLowerCase();

  if (!heldDate || !selDate) return;

  // Vain jos katsotaan juuri sitä päivää, jolle hold tehtiin
  if (heldDate === selDate && status && status !== "pending") {
    clearCooldown();
    setCooldownUntil(null);
    setCooldownLeft(0);
  }
}, [selectedDate?.iso, selectedDate?.status]);

useEffect(() => {
  const onStorage = (e) => {
    if (e.key === "bilebox:quote-cooldown") {
      const c = loadCooldown();
      const until = c?.untilIso ? new Date(c.untilIso).getTime() : 0;
      if (Number.isFinite(until) && until > Date.now()) {
        setCooldownUntil(until);
        setCooldownLeft(Math.max(0, Math.floor((until - Date.now()) / 1000)));
      } else {
        setCooldownUntil(null);
        setCooldownLeft(0);
      }
    }
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}, []);

// Johdettu flagi
const isCoolingDown = Boolean(cooldownUntil && cooldownLeft > 0);

const quoteBlocked = (quoteDisabled || isCoolingDown);

/* ---- Kohteen haku ---- */
useEffect(() => {
  if (!slug || previewStatic) return;  // 👈 esikatselussa ei haeta mitään
  (async () => {
    try {
      if (kind === "company") {
        const { data } = await axios.get(`${BASE}/api/companies/${encodeURIComponent(slug)}`);
        const c = data?.company || data;
        setTargetData(c);

        // moodi + tuntiasetukset WP:stä
        const modeFromCompany =
          (c?.pricing_mode || c?.billing_mode || "").toLowerCase() === "hourly" ||
          (Array.isArray(c?.pricing) && c.pricing.some((p) => p?.hourly_rate != null));
        setBillingMode(modeFromCompany ? "hourly" : "flat");

        setMinHours(Number(c?.min_hours || 1));
        setHourlyRate(
          c?.default_hourly_rate != null ? Number(c.default_hourly_rate) : null
        );

        // aukioloajat
        const hrs = c?.opening_hours || c?.business_hours || c?.hours || null;
        setOpeningHours(parseOpeningHours(hrs));

        // sallitut alueet nimillä (yritykselle suoraan regions-listasta)
        const regNames = Array.isArray(c?.regions)
          ? c.regions
              .map((r) =>
                typeof r === "object" && r ? r.name || r.region || "" : String(r || "")
              )
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        setAllowedRegions(regNames);

        // id->name map
        const mapIdToName = {};
        (c.regions || []).forEach((r) => {
          const id = r?.region_id;
          const name = (r?.name || r?.region || "").trim();
          if (id != null && name) mapIdToName[id] = name;
        });
        setRegionIdToName(mapIdToName);

        // hinnasto (flat / hourly_rate per alue)
        const pricing = Array.isArray(c?.pricing)
          ? c.pricing
              .map((p) =>
                p && typeof p === "object"
                  ? {
                      region_id: Number(p.region_id),
                      price: p.price != null ? Number(p.price) : null,
                      hourly_rate: p.hourly_rate != null ? Number(p.hourly_rate) : null,
                    }
                  : null
              )
              .filter(Boolean)
          : [];
        setCompanyPricing(pricing);

        setAvailableSets([]); // yrityksellä ei settiä
      } else {
        // BAND
        const { data } = await axios.get(
          `${BASE}/api/bands/${encodeURIComponent(slug)}`
        );
        const band = data?.band || data;
        setTargetData(band);

        // --- Kerää setit useasta lähteestä ---
        const fromSets =
          Array.isArray(band.sets)
            ? band.sets.map((s) => s?.label).filter(Boolean)
            : [];

        const fromPricing =
          Array.isArray(band.pricing)
            ? band.pricing.map((p) => p?.set_label).filter(Boolean)
            : [];

        const fromString = String(band.available_sets ?? band.set_options ?? "")
          .split(/[;,|]/)
          .map((s) => s.trim())
          .filter(Boolean);

        // prioriteetti: band.sets (säilytä järjestys) → pricing → string
        const seen = new Set();
        const finalSets = []
          .concat(fromSets, fromPricing, fromString)
          .filter((lbl) => {
            if (!lbl || seen.has(lbl)) return false;
            seen.add(lbl);
            return true;
          });

        // Kaikki bändin setit talteen + oletuksena näytetään kaikki
        setAllBandSets(finalSets);
        setAvailableSets(finalSets);

        // --- Aluekohtainen settilista pricing-taulusta ---
        // Jos alueella on nimettyjä setti-hintoja → näytetään vain ne.
        // Jos alueella on vain "yleishinta" ilman set_labelia → näytetään kaikki finalSets.
        const tmpMap = {};    // { "Uusimaa": Set(["2x45min", ...]) }
        const unlabeled = {}; // { "Uusimaa": true } jos alueella hinnat ilman set_labelia

        (band.pricing || []).forEach((p) => {
          const region = toCanon(p?.region_name || p?.region || "");
          if (!region) return;
          const label = (p?.set_label || "").trim();
          if (label) {
            (tmpMap[region] ||= new Set()).add(label);
          } else {
            unlabeled[region] = true;
          }
        });

        // Viimeistely: jos alueella ei ole nimettyjä settejä mutta on unlabeled-hinta → kaikki setit sallittuja
        const byRegion = {};
        Object.keys(unlabeled).forEach((r) => {
          if (!tmpMap[r] || tmpMap[r].size === 0) byRegion[r] = finalSets.slice();
        });
        Object.entries(tmpMap).forEach(([r, s]) => {
          byRegion[r] = Array.from(s);
        });
        setSetsPerRegion(byRegion);

        // ✅ Sallitut alueet = ne maakunnat, joille ON hinta
        const pricedRegions = Array.isArray(band.pricing)
          ? [
              ...new Set(
                band.pricing
                  .map((p) => (p?.region_name || p?.region || "").trim())
                  .filter(Boolean)
              ),
            ]
          : [];
        setAllowedRegions(pricedRegions);

        setCompanyPricing([]);
        setRegionIdToName({});
      }
    } catch (err) {
      console.error("Virhe kohteen haussa:", err);
        if (!previewStatic) {
        toast.error("Kohdetietojen haku epäonnistui.");
      }
    }
  })();
}, [slug, kind, previewStatic]);

useEffect(() => {
  if (collapsed) return; // älä koske moodiin kiitos-boksissa
  if ((quoteBlocked || hideQuoteOption) && mode === "quote") setMode("message");
}, [quoteBlocked, hideQuoteOption, mode, collapsed]);

// Jos linkissä on pin tai prefill.feedback -> vaihda automaattisesti palautemoodiin
useEffect(() => {
  if (!prefill) return;
  if (prefill.feedback || (prefill.pin && String(prefill.pin).trim())) {
    setMode("feedback");
  }
}, [prefill]);

/* ---- PIN-tarkistus (avaa palautteen) ---- */
const validatePin = useCallback(async () => {
  // 🔒 Estä tuplakutsut: jos tarkistus käynnissä tai jo ok, älä tee mitään
  if (pinStatus === "checking" || pinStatus === "ok") return;

  try {
    setPinStatus("checking");

    const eventDate = eventDateIso;
    if (!eventDate) {
      toast.error("Valitse keikan päivä kalenterista.");
      setPinStatus("idle");
      return;
    }
    if (!form.customer_email.trim()) {
      toast.error("Syötä sähköpostisi.");
      setPinStatus("idle");
      return;
    }
    if (!feedbackPin.trim()) {
      toast.error("Syötä palautekoodi (PIN).");
      setPinStatus("idle");
      return;
    }

    const payload = {
      user_email: form.customer_email.trim().toLowerCase(),
      event_date: eventDate,
      feedback_pin: feedbackPin.replace(/\D/g, ""),
      ...(kind === "band"
        ? { band_id: Number(targetData?.id) }
        : { company_id: Number(targetData?.id) }),
    };

    const r = await fetch(`${BASE}/api/feedback/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    let j = null;
    try { j = await r.json(); } catch {}

    if (r.ok && j?.ok) {
      setPinStatus("ok");
      toast.success("Koodi vahvistettu – palaute auki.");
    } else {
      // Normalisoi palvelimen reason → UI-avain
      const raw = String(j?.reason || "invalid").toLowerCase();
      const reason =
        raw === "not_active_yet" ? "not_active" :
        raw === "not_found"      ? "not_found"  :
        raw;

      setPinStatus(reason);

      if (r.status === 403 && raw === "not_active_yet") {
        // palaute ei ole vielä auki – ei toast-erroria
      } else if (r.status === 410) {
        toast.info("Palautteen aikaikkuna on päättynyt.");
      } else if (r.status === 404) {
        toast.error("Tietoja ei löytynyt annetulla sähköpostilla ja päivällä. Avaa lomake sähköpostin palautelinkistä tai tarkista tiedot.");
      } else {
        toast.error("Palautekoodi ei kelpaa. Käytä sähköpostin palautelinkkiä tai tarkista koodi.");
      }
    }
  } catch (e) {
    setPinStatus("error");
    toast.error("PIN-tarkistus epäonnistui.");
  }
}, [pinStatus, eventDateIso, form.customer_email, feedbackPin, kind, targetData?.id]);


  // korvataan efektin ehto ja riippuvuudet:
useEffect(() => {
  setForm((f) => ({
    ...f,
    message: "",
    gig_length: kind === "band" && mode === "quote" ? f.gig_length : "",
  }));
  setEventPlace("");
  setPlaceSelected(false);
  setLocError("");
  setStars(0);
  setPriceRegion("");
  setPriceForRegion(null);
  setStartTime("");
  setEndTime("");
  setRecaptchaValue(null);
  recaptchaRef.current?.reset?.();

// Palaute-lukon nollaus moodinvaihdossa
if (!(mode === "feedback" && prefillPin)) {
  setFeedbackPin("");
  setPinStatus("idle");
}

}, [mode, kind, prefillPin]);

  useEffect(() => {
    // Parent luo helposti uuden prefill-olion joka renderillä,
    // joten varmistetaan, että tämä ajetaan vain kerran per mount.
    if (!prefill || prefillAppliedRef.current) return;
    prefillAppliedRef.current = true;

    // 1) Email kenttään
    const email = String(prefill.email || "").trim();
    if (email) {
      setForm((f) => ({ ...f, customer_email: f.customer_email || email }));
    }

    // 2) PIN kenttään (vain numerot, 6 merkkiä)
    const pin = String(prefill.pin || "").replace(/\D/g, "");
    if (pin.length === 6) {
      setFeedbackPin(pin);
      setPinStatus("idle"); // sallitaan yksi autovalidointi
    }

    // 3) Vaihda palautemoodiin jos pin tai feedback-lippu on annettu
    if (prefill.feedback || pin) setMode("feedback");
  }, [prefill, setForm, setFeedbackPin, setPinStatus, setMode, prefillAppliedRef]);

/* ---- Google Autocomplete → valinta ---- */
const onPlaceChanged = () => {
  const ac = autocompleteRef.current;
  if (!ac?.getPlace) return;

  const place = ac.getPlace();
  if (!place || !place.address_components) {
    setPlaceSelected(false);
    setLocError("Valitse tapahtumapaikka pudotusvalikosta.");
    setPriceRegion("");
    setPriceForRegion(null);
    return;
  }

  const comps = place.address_components;
  const get = (type) =>
    comps.find((c) => Array.isArray(c.types) && c.types.includes(type))?.long_name || "";

  const regionRaw =
    get("administrative_area_level_1") ||
    get("administrative_area_level_2") ||
    "";

  const canon = toCanon(regionRaw);

  // onko alue sallittu?
  const allowed =
    allowedRegions.length > 0
      ? allowedRegions.some((r) => norm(toCanon(r)) === norm(canon))
      : true;

if (!allowed) {
  setPlaceSelected(false);

  // valinnainen: näytä sallitut alueet lyhyesti
  const regionsHint =
    Array.isArray(allowedRegions) && allowedRegions.length
      ? ` Sallitut alueet: ${allowedRegions.join(", ")}.`
      : "";

  setLocError(
    kind === "band"
      ? `Valitettavasti ${targetName} ei keikkaile tällä alueella (${canon}).${regionsHint}`
      : `Valitettavasti ${targetName} ei tarjoa palvelua tällä alueella (${canon}).${regionsHint}`
  );

  setPriceRegion("");
  setPriceForRegion(null);
  return;
}


  const formatted = place.formatted_address || place.name || "";

  setLocError("");
  setPlaceSelected(true);
  setPriceRegion(canon);
  setEventPlace(formatted);

  // --- Aluekohtainen hinnoittelu: flat tai hourly (YRITYS) ---
  if (kind === "company") {
    // 1) Etsi valitun alueen id nykyisestä id->name -mapista
    const wantedId = Number(
      Object.entries(regionIdToName).find(
        ([, name]) => norm(toCanon(name)) === norm(canon)
      )?.[0]
    );

    // 2) Poimi hinnan rivi tälle alueelle
    const row = companyPricing.find((p) => Number(p.region_id) === wantedId);

    // 3) Päättele aluekohtainen moodi (override tarvittaessa)
    let nextMode = billingMode; // oletus = yrityksen globaali moodi
    const hasFlat   = row?.price != null;
    const hasHourly = row?.hourly_rate != null;

    if (hasFlat && !hasHourly)      nextMode = "flat";
    else if (!hasFlat && hasHourly) nextMode = "hourly";
    // jos molemmat löytyvät → pidä globaali oletus
    // jos kumpaakaan ei ole → validaatio hoitaa lähetysvaiheessa

    // 4) Päivitä moodi vain jos muuttuu (vältä turhia renderöintejä)
    if (nextMode !== billingMode) setBillingMode(nextMode);

    // 5) Aseta hinnat/kentät moodin mukaan
    if (nextMode === "flat") {
      // Näytä/varastoi könttähinta tälle alueelle
      setPriceForRegion(hasFlat ? Number(row?.price) : null);

      // Pidä tuntihinnan tila neutraalina (voi olla globaalina oletuksena tallessa)
      setHourlyRate(
        targetData?.default_hourly_rate != null
          ? Number(targetData.default_hourly_rate)
          : null
      );
    } else {
      // Tuntiveloituksessa hae aluekohtainen tuntihinta,
      // muutoin pudota default_hourly_rateen (jos sellainen on)
      const rate = hasHourly
        ? Number(row?.hourly_rate)
        : (targetData?.default_hourly_rate != null
            ? Number(targetData.default_hourly_rate)
            : null);

      setHourlyRate(rate);
      setPriceForRegion(null); // ei könttähintaa tuntimoodissa
    }
  }
};

  /* ---- Aukioloaikaikkuna valitulle päivälle (yritys) ---- */
  const todaysWindow = useMemo(() => {
    if (kind !== "company" || !openingHours || !selectedDate?.iso) return null;
    const d = new Date(selectedDate.iso);
    if (isNaN(d)) return null;
    const idx = d.getDay(); // 0=Sun
    const key = WEEK_KEYS[idx];
    const win = openingHours[key];
    if (!win || !win.open || !win.close) return null;
    return { open: win.open, close: win.close };
  }, [kind, openingHours, selectedDate?.iso]);

/* ---- Lähetys ---- */
const handleSubmit = async (e) => {
  e.preventDefault();

  // duplikaattisuoja
  if (submitting) return;
  setSubmitting(true);

  // Ennakkoon varattavat muuttujat, joihin viitataan ennen varsinaista holdin luontia
  let holdId = null;
  let holdExpiresIso = null;

  // -- apu: euro-muotoilu (tyhjä jos ei arvoa) --
  const eur = (n) =>
    n == null || Number.isNaN(Number(n))
      ? ""
      : `${Number(n).toFixed(2).replace(/\.00$/, "")} €`;

  try {
    // reCAPTCHA
    // Feedback: jos PIN on valmiiksi validoitu, reCAPTCHA ei ole pakollinen
    const recaptchaActive = !(mode === "feedback" && pinOk) && !BYPASS_RECAPTCHA;
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (recaptchaActive && !siteKey) {
      toast.error("reCAPTCHA-avain puuttuu (.env): REACT_APP_RECAPTCHA_SITE_KEY");
      return;
    }
    if (recaptchaActive && !recaptchaValue) {
      toast.error("Vahvista reCAPTCHA ennen lähettämistä.");
      return;
    }

    // Feedback-keissi: vaadi validi PIN ja päivä
    if (mode === "feedback") {
      if (!pinOk) {
        toast.error("Tarkista palautekoodi (PIN) ennen lähettämistä.");
        return;
      }
      if (!eventDateIso) {
        toast.error("Valitse keikan päivä kalenterista.");
        return;
      }
    }

    

     // --- FEEDBACK-validaatiot -> TÄHÄN LISÄYS ---
    if (mode === "feedback" && stars < 1) {
      toast.error("Valitse arvosana (tähtiä).");
      return;
    }
    if (mode === "feedback" && !form.message.trim()) { toast.error("Kirjoita lyhyt palaute."); return; }
    if (mode === "feedback" && !feedbackPin.trim()) {
      toast.error("Syötä palautekoodi (PIN).");
      return;
    }
    
// (poistettu varhainen onSubmitSuccess-kutsu; kutsu tehdään vasta todellisen onnistumisen jälkeen alempana)


    // Quote-validaatiot
    let formattedDate = null;
    if (mode === "quote") {
      const selected = selectedDate?.iso ? new Date(selectedDate.iso) : null;
      const todayMidnight = new Date(new Date().toDateString());
      if (!selected || isNaN(selected.getTime())) {
        toast.error("Valitse päivä kalenterista ennen lähetystä.");
        return;
      }
      if (selected < todayMidnight) {
        toast.error("Valitse nykyinen tai tuleva päivä.");
        return;
      }
      if (kind === "band" && !form.gig_length) {
        toast.error("Valitse esiintymisen pituus.");
        return;
      }
      if (!eventPlace) {
        toast.error("Lisää tapahtumapaikka.");
        return;
      }
      if (mapsReady && !placeSelected) {
        toast.error("Valitse tapahtumapaikka pudotusvalikosta.");
        return;
      }
      if (!priceRegion) {
        toast.error("Alueen määritys epäonnistui. Valitse tapahtumapaikka uudelleen.");
        return;
      }

      // yrityskohtaiset tarkistukset
      if (kind === "company") {
        if (billingMode === "flat") {
          if (priceForRegion == null || isNaN(priceForRegion)) {
            toast.error("Tälle alueelle ei löytynyt yrityksen hinnastoa. Valitse toinen alue tai ota yhteyttä.");
            return;
          }
        } else {
          // hourly
          if (!startTime || !endTime) {
            toast.error("Valitse aloitus- ja lopetusaika.");
            return;
          }
          const toMins = (t) => {
            const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(t || "").trim());
            return m ? +m[1] * 60 + +m[2] : null;
          };
          const a = toMins(startTime), b = toMins(endTime);
          const hoursBookedLocal = (a == null || b == null || b <= a) ? 0 : (b - a) / 60;

          if (hoursBookedLocal <= 0) {
            toast.error("Lopetusajan tulee olla aloituksen jälkeen.");
            return;
          }
          const within = (t) => {
            if (!todaysWindow) return true;
            const o = toMins(todaysWindow.open);
            const c = toMins(todaysWindow.close);
            const x = toMins(t);
            if (o == null || c == null || x == null) return true;
            return x >= o && x <= c;
          };
          if (!within(startTime) || !within(endTime)) {
            const w = todaysWindow ? ` (${todaysWindow.open}–${todaysWindow.close})` : "";
            toast.error(`Valitut ajat ovat yrityksen aukiolojen ulkopuolella${w}.`);
            return;
          }
          if (hourlyRate == null || Number(hourlyRate) <= 0) {
            toast.error("Tuntihinta puuttuu tälle alueelle.");
            return;
          }
        }
      }

      formattedDate = new Date(selectedDate.iso).toISOString().split("T")[0];
    }


    // pp/kk/vvvv – käytä en-GB:ä jotta saat kauttaviivat
      let eventDateFi = "";
      if (mode === "quote" && selectedDate?.iso) {
        const d = new Date(selectedDate.iso + "T12:00:00"); // “keskipäivä” -> ei tz-driftiä
        eventDateFi = new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(d); // esim. 26/09/2025
      }

    // Etäisyys (vain bändit + quote)
    let distanceKm = 0;
    let durationText = "–";
    if (kind === "band" && mode === "quote" && targetData?.band_address && eventPlace) {
      const { distance, duration } = await getDistanceAndDuration(
        targetData.band_address,
        eventPlace
      );
      distanceKm = distance || 0;
      const mins = Math.round((duration || 0) * 60);
      const hrs = Math.floor(mins / 60);
      const rem = mins % 60;
      durationText = `${hrs > 0 ? `${hrs} h ` : ""}${rem > 0 ? `${rem} min` : ""}`.trim() || "–";
    }

    // Hinta (bändi API:sta, yritys suoraan)
    let priceNow = null;
    if (mode === "quote") {
      if (kind === "band") {
        const { data } = await axios.get(`${BASE}/api/bookings/price`, {
          params: {
            slug,
            region: priceRegion,          // esim. "Uusimaa"
            duration: form.gig_length,    // esim. "2x45min"
            distance: distanceKm          // km, yhteen suuntaan
          },
        });
        // TUE MOLEMMAT: { ok: true } TAI { success: true }
        const ok = data?.ok === true || data?.success === true;
        if (!ok) {
          throw new Error(data?.error || "Hinnan haku epäonnistui");
        }
        priceNow = {
          base_price: Number(data.base_price ?? 0),
          surcharge:  Number(data.surcharge  ?? 0),
          total_price:Number(data.total_price?? 0),
        };
      } else {
        const round2 = (n) => Math.round((+n + Number.EPSILON) * 100) / 100;
        if (billingMode === "flat") {
          const total = Number(priceForRegion || 0);
          priceNow = { base_price: total, surcharge: 0, total_price: total, pricing_mode: "flat" };
        } else {
          const billable = Math.max(
            (() => {
              const toMins = (t) => {
                const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(t || "").trim());
                return m ? +m[1] * 60 + +m[2] : null;
              };
              const a = toMins(startTime), b = toMins(endTime);
              return (a == null || b == null || b <= a) ? 0 : (b - a) / 60;
            })(),
            minHours
          );
          const total = round2(billable * Number(hourlyRate || 0));
          priceNow = {
            base_price: total,
            surcharge: 0,
            total_price: total,
            pricing_mode: "hourly",
            hours: billable,
            hourly_rate: Number(hourlyRate || 0),
            start_time: startTime,
            end_time: endTime,
          };
        }
      }
    }

    // --- Välityspalkkio & varausmaksu (vain bändit/Duo/DJ/trubaduuri) ---
let commissionPct = null;
let commissionLabel = "";
let commissionAmount = null;   // €
let depositPct = null;
let depositLabel = "";
let depositAmount = null;

const round2 = (n) => Math.round((+n + Number.EPSILON) * 100) / 100;

if (mode === "quote" && kind === "band" && priceNow) {
  commissionPct    = commissionPctFor(targetData);            // 0.15 tai 0.12
  commissionLabel  = `${Math.round(commissionPct * 100)} %`;
  commissionAmount = round2(Number(priceNow.base_price || 0) * commissionPct);

  // Varausmaksu = sama prosentti (lasketaan vain esiintymispalkkiosta, ei km-kuluista)
  depositPct    = commissionPct;
  depositLabel  = commissionLabel;
  depositAmount = commissionAmount;
}

// HOLD (vain quote) – muuttujat holdId ja holdExpiresIso alustettu ylempänä
let holdExpiresFi = "";
let ttlLabel = HOLD_TTL_HOURS === 1 ? "1 h" : `${HOLD_TTL_HOURS} h`;
if (mode === "quote") {
  const payload = {
    customer_name: form.customer_name,
    customer_email: form.customer_email,
    customer_phone: form.customer_phone,
    event_date: formattedDate,
    event_location: eventPlace,

    // --- Alue ---
    region:       priceRegion,
    region_name:  priceRegion,        // alias templatelle
    region_label: priceRegion,        // alias templatelle

    // --- Hinnat numerona (BE/templalle) ---
    base_price:       String(priceNow?.base_price ?? 0),
    base_price_num:    Number(priceNow?.base_price ?? 0),
    surcharge:        String(priceNow?.surcharge ?? 0),      // matkakulut
    surcharge_num:     Number(priceNow?.surcharge ?? 0),
    total_price:      String(priceNow?.total_price ?? 0),
    total_price_num:   Number(priceNow?.total_price ?? 0),
    price:            String(priceNow?.total_price ?? 0),    // legacy

    // --- Ihmisen luettavat €-tekstit (helpottaa templaa) ---
    base_price_text:  priceNow ? eur(priceNow.base_price)  : "",
    surcharge_text:   priceNow ? eur(priceNow.surcharge)   : "",
    total_price_text: priceNow ? eur(priceNow.total_price) : "",

    // --- Alias-nimet, jos templassa eri avaimet ---
    travel_cost:      String(priceNow?.surcharge ?? 0),
    travel_cost_text: priceNow ? eur(priceNow.surcharge) : "",

    recaptcha_token: recaptchaValue,
  };

  if (kind === "band") {
    payload.band_id = targetData?.id ?? null;
    payload.gig_length = form.gig_length;

    // Bändille: matka & kesto templaa/BE:tä varten
    payload.distance_km       = Number((distanceKm ?? 0).toFixed(1));
    payload.distance_total_km = Number(((distanceKm ?? 0) * 2).toFixed(1));
    payload.duration_text     = durationText || "";
  } else {
    payload.company_id = targetData?.id ?? null;
    payload.service_summary = Array.isArray(targetData?.services) ? targetData.services.join(", ") : "";
    payload.pricing_mode = billingMode;
    if (billingMode === "hourly") {
      payload.start_time  = startTime;
      payload.end_time    = endTime;
      payload.hours       = String(priceNow?.hours ?? "");
      payload.hourly_rate = String(hourlyRate ?? 0);
    }
  }

  const res = await fetch(`${BASE}/api/bookings/booking`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let json = null;
  try { json = JSON.parse(text); } catch {}
  if (!res.ok || !json?.success) {
    throw new Error(json?.error || "Tietokantavirhe");
  }
  holdId = String(json.hold_id);

  // --- Vanhenemishetki: käytä serverin expires_at, muuten fallback env:iin ---
  const expiresIso = json.expires_at || null;
  const expDate = expiresIso
    ? new Date(expiresIso)
    : new Date(Date.now() + HOLD_TTL_HOURS * 3600e3);
 holdExpiresFi = expDate.toLocaleString("fi-FI", {
   day: "2-digit", month: "2-digit", year: "numeric",
   hour: "2-digit", minute: "2-digit",
   timeZone: "Europe/Helsinki",          // 👈 estää tz-driftin
 });
 holdExpiresIso = expDate.toISOString(); // 👈 talteen ISO
  if (expiresIso) {
    const msLeft = expDate.getTime() - Date.now();
    const approxHours = Math.max(1, Math.round(msLeft / 3600e3));
    ttlLabel = approxHours === 1 ? "1 h" : `${approxHours} h`;
  }

  // Käynnistä FE-cooldown samaan hetkeen kuin holdin vanheneminen
try {
  // talleta myös päivän ISO (yyyy-mm-dd) jotta tiedetään mihin holdiin FE-esto liittyy
const holdDateIso = (selectedDate?.iso || "").slice(0, 10);
saveCooldown({ untilIso: holdExpiresIso, holdId, date: holdDateIso });

  setCooldownUntil(expDate.getTime());
  setCooldownLeft(Math.max(0, Math.floor((expDate.getTime() - Date.now()) / 1000)));
} catch {}

 // 🔔 PING: kalenterin refresh (sama välilehti + muut välilehdet)
 try {
   localStorage.setItem("bilebox:calendar-touched", String(Date.now()));
   window.dispatchEvent(new Event("bb:calendar:touched"));
 } catch {}
}

    // Linkit maileihin (vain quote)
const confirmUrl =
  mode === "quote" && holdId
    ? `${API_BASE_ABS}/api/bookings/confirm-redirect?t=${encodeURIComponent(holdId)}&a=confirm`
    : "";

const cancelUrl =
  mode === "quote" && holdId
    ? `${API_BASE_ABS}/api/bookings/confirm-redirect?t=${encodeURIComponent(holdId)}&a=cancel`
    : "";


    // Vastaanottajat
    const customerTo = (form.customer_email || "").trim();
    if (!customerTo) {
      toast.error("Sähköposti puuttuu.");
      return;
    }
    const ownerTo =
      (kind === "company" ? (targetData?.company_email || "") : (targetData?.band_email || ""))
        .trim() || (process.env.REACT_APP_OWNER_FALLBACK_EMAIL || "").trim();
    // Owner-sähköpostia ei vaadita palautteelle/viestille — varoita vain ja jatka
    if (mode !== "quote" && !ownerTo) {
      // ei toast-erroria joka estäisi lähetystä
      console.warn("[Email] Owner-sähköposti puuttuu — ohitetaan notifikaatio.");
    }

    // Otsikot erikseen asiakkaalle ja omistajalle
    const subjectCustomer =
      mode === "message"
        ? (kind === "company"
            ? `Postia toimijalle ${targetName} henkilöltä ${form.customer_name || "Asiakas"}`
            : `Postia esiintyjälle ${targetName} henkilöltä ${form.customer_name || "Asiakas"}`)
        : mode === "feedback"
          ? `Palaute: ${targetName} – ${form.customer_name || "Asiakas"}`
          : `Tarjouspyyntö: ${targetName}${selectedDate?.formatted ? " – " + selectedDate.formatted : ""}`;

    const subjectOwner =
      `${mode === "quote" ? "Tarjouspyyntö" : mode === "message" ? "Viesti" : "Palaute"}: ${targetName} — ilmoitus`;

    // ----- EmailJS-parametrit -----
    // HUOM: käytetään string-lippuja, koska osa templatemoottereista
    // kohtelee "false" (string) totuusarvona.
    const flag = (cond) => (cond ? "1" : ""); // "1" = tosi, "" = falsy

    // Yhtenäinen artistikategoria templaa / flaggeja varten
    const artistCategory =
      (kind === "band") ? categorizeArtist(targetData) : "";


    const baseParams = {
      // liput
      is_quote:   flag(mode === "quote"),
      is_message: flag(mode === "message"),
      is_feedback: flag(mode === "feedback"),
      is_company: flag(kind === "company"),
      is_band:    flag(kind === "band"),
      show_rating: flag(mode === "feedback"),
      rating:         mode === "feedback" ? String(stars) : "",
      stars_visual:   mode === "feedback" ? ("★".repeat(stars) + "☆".repeat(5 - stars)) : "",
      // Yhteensopivuus nykyisen email-templaten kanssa:
      rating_value:   mode === "feedback" ? String(stars) : "",
      rating_stars:   mode === "feedback" ? ("★".repeat(stars) + "☆".repeat(5 - stars)) : "",

      // hinnoittelu
      pricing_mode: kind === "company" ? (billingMode || "") : "",
      billing_mode: kind === "company" ? (billingMode || "") : "",
      billing_mode_label: kind === "company" ? (billingMode === "hourly" ? "Tuntiveloitus" : "Könttähinta") : "",
      is_hourly: flag(kind === "company" && mode === "quote" && billingMode === "hourly"),
      is_flat:   flag(kind === "company" && mode === "quote" && billingMode === "flat"),

      // asiakas
      user_name: form.customer_name || "",
      user_email: customerTo,
      user_phone: form.customer_phone || "",
      user_message: form.message || "",

      // kohde
      target_type: targetTypeLabel,
      target_name: targetName,
      target_or_band: targetName,
      target_email: ownerTo,
      target_phone: (kind === "company" ? targetData?.company_phone : targetData?.band_phone) || "",
      target_address: (kind === "company" ? targetData?.company_address : targetData?.band_address) || "",

      // tapahtuma (tyhjät kun ei quote)
      event_date:     mode === "quote" ? (formattedDate || "") : "",
      event_date_fi:  mode === "quote" ? (eventDateFi   || "") : "",   // pp/kk/vvvv
      event_location:  mode === "quote" ? (eventPlace || "") : "",
      gig_length:      (kind === "band" && mode === "quote") ? (form.gig_length || "") : "",

      // lisäkentät (tyhjät kun ei relevantteja)
      region:        mode === "quote" ? (priceRegion || "") : "",
      price_region:  mode === "quote" ? (priceRegion || "") : "",
      opening_hours_text: (mode === "quote" && todaysWindow) ? `${todaysWindow.open}–${todaysWindow.close}` : "",
      min_hours: kind === "company" ? (minHours ?? "") : "",
      service_summary: (kind === "company"
        ? (Array.isArray(targetData?.services) ? targetData.services.join(", ") : "")
        : ""),
      // HOLD-viestit
      hold_expires_fi: holdExpiresFi,
      hold_expires_iso: holdExpiresIso,      // 👈 tämä korvaa expDate.toISOString()
      hold_ttl_hours: String(HOLD_TTL_HOURS),
      hold_ttl_label: ttlLabel,

      // hinnat (tyhjät jos ei quote)
      base_price:  mode === "quote" && priceNow ? eur(priceNow.base_price)  : "",
      price:       mode === "quote" && priceNow ? eur(priceNow.base_price)  : "",
      surcharge:   mode === "quote" && priceNow ? eur(priceNow.surcharge)   : "",
      total_price: mode === "quote" && priceNow ? eur(priceNow.total_price) : "",

      base_price_num:  mode === "quote" && priceNow ? Number(priceNow.base_price)  : null,
      surcharge_num:   mode === "quote" && priceNow ? Number(priceNow.surcharge)   : null,
      total_price_num: mode === "quote" && priceNow ? Number(priceNow.total_price) : null,

      // bändin matkat
      distance:       (kind === "band" && mode === "quote" && distanceKm != null) ? distanceKm.toFixed(1) : "",
      distance_total: (kind === "band" && mode === "quote" && distanceKm != null) ? (distanceKm * 2).toFixed(1) : "",
      duration_text:  (kind === "band" && mode === "quote") ? durationText : "",

      // tuntiveloitus
      start_time:       (kind === "company" && mode === "quote" && billingMode === "hourly") ? startTime : "",
      end_time:         (kind === "company" && mode === "quote" && billingMode === "hourly") ? endTime   : "",
      hours_text: (kind === "company" && mode === "quote" && billingMode === "hourly" && priceNow?.hours != null)
  ? `${priceNow.hours} h`
  : "",

      hourly_rate_text: (kind === "company" && mode === "quote" && billingMode === "hourly" && hourlyRate != null) ? `${Number(hourlyRate).toFixed(2)} € / h` : "",

      // --- Välityspalkkio & varausmaksu (vain bändit/Duo/DJ/trubaduuri) ---
      commission_pct:         (mode === "quote" && kind === "band" && commissionPct != null)       ? String(Math.round(commissionPct * 100)) : "",
      commission_pct_text:    (mode === "quote" && kind === "band" && commissionPct != null)       ? commissionLabel : "",
      commission_amount:      (mode === "quote" && kind === "band" && commissionAmount != null)    ? eur(commissionAmount) : "",
      commission_amount_num:  (mode === "quote" && kind === "band" && commissionAmount != null)    ? commissionAmount : null,
      deposit_pct:            (mode === "quote" && kind === "band" && depositPct != null)          ? String(Math.round(depositPct * 100)) : "",
      deposit_pct_text:       (mode === "quote" && kind === "band" && depositPct != null)          ? depositLabel : "",
      deposit_amount:         (mode === "quote" && kind === "band" && depositAmount != null)       ? eur(depositAmount) : "",
      deposit_amount_num:     (mode === "quote" && kind === "band" && depositAmount != null)       ? depositAmount : null,


      // Kategoriat templaten ehtoja varten
      artist_category: artistCategory,

      // Boole-flagit templaten ehtoihin (vain bändeille)
      is_trubaduuri: (kind === "band" && artistCategory === "trubaduuri") ? "1" : "",
      is_dj:         (kind === "band" && artistCategory === "dj")         ? "1" : "",
      is_bilebandi:  (kind === "band" && artistCategory === "bilebandi")  ? "1" : "",
      is_duo:        (kind === "band" && artistCategory === "duo")        ? "1" : "",

      commission_base_note:
        (mode === "quote" && kind === "band")
          ? "Välityspalkkio/varausmaksu lasketaan esiintymispalkkiosta (ei matkakuluista)."
          : "",

      // CTA-linkit (vain quote)
      confirm_url: mode === "quote" ? (confirmUrl || "") : "",
      cancel_url:  mode === "quote" ? (cancelUrl  || "") : "",

      // email metadata
      reply_to: customerTo,
      to_email: "",
      from_name: "Esiintyjävälitys Buukkeri – ilmoitukset",
      from_email: "notifications@buukkeri.com",
    };

    const bandFields = kind === "band"
      ? {
          band_name: targetName,
          band_email: targetData?.band_email || ownerTo || "",
          band_phone: targetData?.band_phone || "",
          band_address: targetData?.band_address || "",
        }
      : {};

    const companyFields = kind === "company"
      ? {
          target_name: targetName,
          target_email: targetData?.company_email || ownerTo || "",
          target_phone: targetData?.company_phone || "",
          target_address: targetData?.company_address || "",
        }
      : {};


    const paramsCustomer = {
       ...baseParams, ...bandFields, ...companyFields,

      to_email: customerTo,
      email: customerTo, // alias, jos templaatti käyttää {{email}}
       subject: subjectCustomer
     };

    const paramsOwner = {
       ...baseParams, ...bandFields, ...companyFields,

      to_email: ownerTo,
      email: ownerTo,     // alias, jos templaatti käyttää {{email}}
       bcc: "",
       subject: subjectOwner
     };

// LÄHETYKSET:
// - Asiakas: vain quote
// - Kohde (esiintyjä/yritys): VIestissä heti, palautteessa vasta tallennuksen jälkeen
const sendJobs = [];
if (mode === "quote") {
  sendJobs.push(
    safeEmailSend(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_CUSTOMER_PERFORMER,
      paramsCustomer
    )
  );
}

// Viestitila: ilmoitus esiintyjälle heti
if (mode === "message" && ownerTo) {
  sendJobs.push(
    safeEmailSend(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID_PERFORMER, paramsOwner)
  );
}

// HOLD-ilmoitus lähetetään back-endistä (owner + manager -rooleille).
// Ei lähetetä enää FE:stä.

await Promise.allSettled(sendJobs);

  // PALAUTE → (bändi TAI yritys)
  if (mode === "feedback") {
    try {
      const body = {
        user_name:  form.customer_name?.trim(),
        user_email: form.customer_email?.trim().toLowerCase(),
        rating:     Math.max(1, Math.min(5, Math.round(Number(stars)))),
        feedback:   form.message?.trim(),
        ...(kind === "band"
          ? { band_id: Number(targetData?.id) }
          : { company_id: Number(targetData?.id) }),
        event_date: eventDateIso,
        feedback_pin: feedbackPin.replace(/\D/g, ""),                  // 👈 PIN mukaan
      };

const res = await fetch(`${BASE}/api/feedback/submit`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

// --- Kestävä parsinta: osa 2xx-vastauksista voi olla tyhjiä (204/201) ---
const raw = await res.text();
let json = null;
try { json = raw ? JSON.parse(raw) : null; } catch {}

// Yritä lukea syy myös headereista, jos body puuttuu
const reason = String(
  json?.reason ||
  res.headers.get("x-feedback-reason") ||
  res.headers.get("x-reason") ||
  ""
).toLowerCase();

// Hyväksy 204/201 ja 2xx ilman JSONia tai {ok/success:true}
const okish =
  res.status === 204 || res.status === 201 ||
  (res.ok && (json == null || json.success === true || json.ok === true));

if (!okish) {
  console.warn("Feedback save failed", {
    status: res.status,
    statusText: res.statusText,
    reason,
    response: json ?? raw ?? "(empty)",
    sentBody: body,
  });

  // 🔁 UUSI: palaute on jo kannassa → lähetä ilmoitus esiintyjälle silti
  if (res.status === 409 && (reason === "already_submitted" || reason === "duplicate")) {
    try {
      if (ownerTo) {
        const tmplId = EMAILJS_TEMPLATE_ID_PERFORMER_FEEDBACK || EMAILJS_TEMPLATE_ID_PERFORMER;
        await safeEmailSend(EMAILJS_SERVICE_ID, tmplId, paramsOwner);
        toast.info("Palaute oli jo annettu – lähetin ilmoituksen esiintyjälle uudelleen.");
      } else {
        console.warn("[Feedback duplicate] ownerTo puuttuu – ei voitu uudelleenlähettää.");
        toast.info("Palaute oli jo annettu.");
      }
    } catch (e) {
      console.warn("EmailJS redispatch failed (duplicate)", e);
      toast.warn("Palaute on jo annettu. Ilmoituksen uudelleenlähetys epäonnistui.");
    }
    return; // ei jatketa “Kiitos”-flow’hun, koska tallennus ei tapahtunut nyt
  }

  // Muut virheet entiseen tapaan
  if (res.status === 401 || reason === "invalid_pin" || reason === "pin_mismatch") {
    toast.error("Väärä palautekoodi (PIN). Tarkista koodi palautekyselystä tai avaa lomake sähköpostin palautelinkistä.");
  } else if (res.status === 409 && (reason === "not_active_yet" || reason === "not_active")) {
    const when = json?.activation_at
      ? new Date(json.activation_at).toLocaleString("fi-FI", { timeZone: "Europe/Helsinki" })
      : "keikkapäivänä";
    toast.info(`Palaute aukeaa ${when}.`);
  } else if (res.status === 410 || reason === "expired") {
    toast.info("Palautteen aikaikkuna on päättynyt.");
  } else if (res.status === 404) {
    toast.error("Varausta ei löytynyt tällä koodilla ja sähköpostilla.");
  } else if (res.status === 429) {
    toast.warn("Liikaa yrityksiä – yritä hetken kuluttua uudelleen.");
  } else {
    toast.warn(`Palautteen tallennus epäonnistui (${res.status}).`);
  }
  return; // estä “Kiitos”-flow virheessä
}

// ✅ Tallennus OK → lähetä nyt palautemaili esiintyjälle/yritykselle
try {
  if (ownerTo) {
    const tmplId = EMAILJS_TEMPLATE_ID_PERFORMER_FEEDBACK || EMAILJS_TEMPLATE_ID_PERFORMER;
    await safeEmailSend(EMAILJS_SERVICE_ID, tmplId, paramsOwner);
  }
} catch (e) {
  console.warn("EmailJS (feedback) fail", e);
}

} catch (err) {
  console.warn("Feedback request error", err);
  toast.warn("Palautteen tallennus epäonnistui.");
  return; // sama syy kuin yllä
}

  }

   const toastMap = {
  quote: kind === "company"
    ? [
        "Kiitos! Tarjouspyyntösi yritykselle on vastaanotettu!",
        `Vahvistuslinkki on lähetetty sähköpostiisi (voimassa ${ttlLabel}).`
      ]
    : [
        "Kiitos! Tarjouspyyntösi on vastaanotettu!",
        `Vahvistuslinkki on lähetetty sähköpostiisi (voimassa ${ttlLabel}).`
      ],
  message: [
    "Viestisi lähti matkaan!",
    "esiintyjä palaa asiaan pian."
  ],
  feedback: [
    "Kiitos palautteesta!",
    "Arvostelusi nostaa esiintyjän näkyvää arvosanaa."
  ],
};

    const [t1, t2] = toastMap[mode] || ["Valmista!", ""];
    toast.success(`${t1} ${t2}`.trim(), { containerId: "global", autoClose: 9000 });

    // 🔒 Lukitaan kiitos-boksille lähetetty moodi ja metat
    setLastSubmitMode(mode);
    setLastSubmitMeta({
      email: (form.customer_email || "").trim(),
      ttlLabel,       // esim. "1 h"
      holdExpiresFi   // esim. "27.10.2025 klo 21:45"
    });

    // Reset
    setForm({ customer_name: "", customer_email: "", customer_phone: "", message: "", gig_length: "" });
    setEventPlace("");
    setPlaceSelected(false);
    setLocError("");
    setStars(0);
    setPriceRegion("");
    setPriceForRegion(null);
    setStartTime("");
    setEndTime("");
    setRecaptchaValue(null);
    setPinStatus("idle");
    setFeedbackPin("");
    if (recaptchaRef.current && typeof recaptchaRef.current.reset === "function") {
      recaptchaRef.current.reset();

    }

    // Ilmoita parentille vasta kun kaikki kriittiset stepit onnistuivat
    try {
      onSubmitSuccess?.({
        mode,                         // 'quote' | 'message' | 'feedback'
        kind,                         // 'band' | 'company'
        date: (selectedDate?.iso || prefill?.date || "").slice(0, 10),
        status: mode === "quote" ? "pending" : "sent",
        holdId: mode === "quote" ? holdId : null,
        holdExpiresIso: mode === "quote" ? holdExpiresIso : null,
      });
    } catch (e) {
      console.warn("onSubmitSuccess handler threw", e);
    }

    // Sulje UI ja scrollaa "Kiitos" -boksi näkyviin (mobiili) – vain jos sallittu
    if (!hideSuccessCard) {
      setCollapsed(true);
    }
    setTimeout(() => {
      const el = document.getElementById("contact-form");
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        try { el.focus({ preventScroll: true }); } catch {}
      }
    }, 0);
    try {
      const url = new URL(window.location.href);
      ["feedback","email","date","pin"].forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.toString());
    } catch {}

  } catch (err) {
    toast.error("Jotain meni pieleen: " + (err?.message || "Tuntematon virhe"), { containerId: "global" });
  } finally {
    setSubmitting(false);
  }
};

// Submit-eston logiikka: estä myös cooldownin aikana
const submitDisabled =
  (mode === "quote"    && (isCoolingDown || (mapsReady && !placeSelected))) ||
  (mode === "feedback" && !pinOk) ||
  submitting;

// Tilannekohtainen vihjeteksti Submit-napille
const submitReason =
  submitting ? "Lähetys käynnissä…" :
  (mode === "quote" && isCoolingDown)
    ? "Tarjouspyyntö on lukittu, kunnes vahvistat tai perut sähköpostista."
    : (mode === "quote" && enableMaps && !placeSelected)
      ? ""
      : (mode === "feedback" && !pinOk)
        ? ""
        : undefined;

  // Johdetut: yksi truthy estolippu ja perusteluviesti
const effectiveDisabled =
  submitting || submitDisabled || offline || previewStatic;

  const btnReason =
    offline
      ? "Ei verkkoyhteyttä – tarkista yhteys."
      : submitReason; // ei viestiä, kun submitting===true

  // Palauteosio lukkoon, kunnes PIN ok
  const feedbackLocked = mode === "feedback" && !pinOk;
   
  return (
  <div id="contact-form" data-contact-form className="scroll-anchor max-w-md w-full mx-auto px-2 sm:px-4 lg:px-8" tabIndex="-1">
    {collapsed && !hideSuccessCard ? (
      <div className="rounded-xl p-4 bg-emerald-900/40 text-emerald-50">
        <p className="font-semibold">Kiitos!</p>
        <div className="text-sm mt-1 space-y-1">
          {lastSubmitMode === "feedback" && (
            <p>Palautteesi on tallennettu.</p>
          )}

          {lastSubmitMode === "message" && (
            <p>Viestisi on lähetetty – esiintyjä palaa asiaan pian.</p>
          )}

          {lastSubmitMode === "quote" && (
            <>
              <p>
                Tarjouspyyntö vastaanotettu. Vahvistuslinkki on lähetetty{" "}
                {lastSubmitMeta?.email
                  ? <>osoitteeseen <b>{lastSubmitMeta.email}</b></>
                  : "sähköpostiisi"}
                .
              </p>
              <p>
                Linkki on voimassa {lastSubmitMeta?.ttlLabel || "1 h"}
                {lastSubmitMeta?.holdExpiresFi ? (
                  <> (asti {lastSubmitMeta.holdExpiresFi})</>
                ) : null}
                .
              </p>
            </>
          )}

          {/* Fallback – jos jostain syystä lukittua moodia ei ole */}
          {!lastSubmitMode && <p>Viestisi on lähetetty.</p>}
        </div>
      </div>
    ) : (

      <form
        onSubmit={handleSubmit}
        className="space-y-4 bg-lemon/60 dark:bg-night-700/50 dark:bg-opacity-75 bg-opacity-75 rounded-lg p-3 sm:p-5 mb-4"
      >
      {/* Moodivalitsin */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="dark:text-primary/80 text-black/80 font-customFont1 font-semibold">
                Valitse lomake:
              </label>
              <select
                className="w-full sm:flex-1 p-2 rounded-xl bg-white text-black/80 dark:bg-darkest dark:text-black/80"
                value={mode}
                onChange={(e) => {
                  const next = e.target.value;
                  if ((quoteBlocked || hideQuoteOption) && next === "quote") return;
                  setMode(next);
                }}
              >
                {!hideQuoteOption && (
                  <option
                    value="quote"
                    disabled={quoteBlocked || isPastSelected}
                  >
                    Tarjouspyyntö
                  </option>
                )}
                <option value="message">Viesti</option>
                <option value="feedback">Palaute</option>
              </select>
            </div>

            {/* Selite tarjouspyynnön rajoituksista */}
            {!hideQuoteOption && (
              <p className="mt-1 text-xs text-black/60 dark:text-white/60">
                {quoteBlocked
                  ? "Olet jo lähettänyt tarjouspyynnön tälle esiintyjälle. Uuden voit lähettää, kun aiempi on vahvistettu tai peruttu (viimeistään noin tunnin kuluttua)."
                  : "Voit pitää kerrallaan yhden tarjouspyynnön aktiivisena tälle esiintyjälle."}
              </p>
            )}
        
         {mode === "quote" &&
  !collapsed &&
  lastSubmitMode !== "quote" &&
  !hideInlineBanner &&
  banner && (
    <div
      className={
        "rounded-xl p-3 text-sm mb-2 " +
        (banner.type === "ok"
          ? "bg-emerald-800/30 text-emerald-100"
          : banner.type === "warn"
          ? "bg-amber-800/30  text-amber-100"
          : "bg-rose-800/30   text-rose-100")
      }
    >
      {banner.text}
    </div>
)}


        {/* Tarjouspyyntö */}
        {mode === "quote" && !quoteDisabled && (
          <div className="space-y-3">
            {/* Keikan pituus vain bändeille */}
            {kind === "band" && availableSets.length > 0 && (
              <>
                <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold">
                  Valitse esiintymisen pituus:
                </label>
                <div className="flex flex-wrap justify-center gap-3">
                  {availableSets.map((set, i) => {
                    const id = `gig_len_${i}`;
                    return (
                      <label key={id} htmlFor={id} className="flex items-center gap-2">
                        <input
                          id={id}
                          type="radio"
                          name="gig_length"
                          value={set}
                          checked={form.gig_length === set}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, gig_length: e.target.value }))
                          }
                          required={kind === "band" && availableSets.length > 0}
                        />
                        <span className="dark:text-primary/80 text-black/80 font-customFont1 font-semibold">
                          {set}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {/* Tapahtumapaikka */}
            <div>
              <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
                Tapahtumapaikka:
              </label>
              {enableMaps ? (
                isLoaded ? (
                  <Autocomplete
                    onLoad={(a) => (autocompleteRef.current = a)}
                    onPlaceChanged={onPlaceChanged}
                    options={{
                      componentRestrictions: { country: "fi" },
                      fields: ["address_components", "formatted_address", "name"],
                    }}
                  >
                    <input
                      id="event_location"
                      type="text"
                      placeholder="Kirjoita osoite ja valitse listasta…"
                      value={eventPlace}
                      onChange={(e) => {
                        setEventPlace(e.target.value);
                        setPlaceSelected(false);
                        setLocError("");
                        setPriceRegion("");
                        setPriceForRegion(null);
                      }}
                      required
                      data-scroll-anchor
                      aria-invalid={!!locError}
                      aria-describedby={locError ? "event_location_error" : undefined}
                      className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10 text-black/90 dark:text-white/90 placeholder-black/40 dark:placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pink/60"
                    />
                  </Autocomplete>
                ) : (
                  <input
                    id="event_location"
                    type="text"
                    placeholder={
                      loadError
                        ? "Kirjoita tapahtumapaikka…"
                        : "Ladataan Googlea… voit silti kirjoittaa"
                    }
                    value={eventPlace}
                    onChange={(e) => {
                      setEventPlace(e.target.value);
                      setPlaceSelected(false);
                      setLocError("");
                      setPriceRegion("");
                      setPriceForRegion(null);
                    }}
                    required
                    aria-invalid={!!locError}
                    aria-describedby={locError ? "event_location_error" : undefined}
                    className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10 text-black/90 dark:text-white/90 placeholder-black/40 dark:placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pink/60"
                  />
                )
              ) : (
                <input
                  type="text"
                  placeholder={
                    MAPS_DEV_FAKE
                      ? "Dev-tila: kirjoita tapahtumapaikka (ei Googlea)…"
                      : "Kirjoita tapahtumapaikka…"
                  }
                  value={eventPlace}
                  onChange={(e) => setEventPlace(e.target.value)}
                  required
                  aria-invalid={!!locError}
                  aria-describedby={locError ? "event_location_error" : undefined}
                  className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10 text-black/90 dark:text-white/90 placeholder-black/40 dark:placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pink/60"
                />
              )}
              {locError && (
                <p
                  id="event_location_error"
                  className="mt-2 font-customFont1 text-xs font-normal text-rose-600 dark:text-rose-300"
                >
                  {locError}
                </p>
              )}
            </div>

            {/* Tuntiveloitus (yritys) */}
            {kind === "company" && billingMode === "hourly" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
                    Aloitusaika
                  </label>
                  <input
                    type="time"
                    step="900"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                    className="w-full p-3 rounded-xl bg-white dark:bg-darkest dark:text-black/80"
                  />
                </div>
                <div>
                  <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
                    Lopetusaika
                  </label>
                  <input
                    type="time"
                    step="900"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                    className="w-full p-3 rounded-xl bg-white dark:bg-darkest dark:text-black/80"
                  />
                </div>

                {/* Ei näytetä hintaa — vain neutraali ohjeistus */}
                <div className="col-span-2 text-center text-xs sm:text-sm font-semibold dark:text-white/80 text-black/80">
                  {todaysWindow
                    ? `Aukiolo tänä päivänä: ${todaysWindow.open}–${todaysWindow.close}. Minimiveloitus ${minHours} h.`
                    : `Valitse ajat. Minimiveloitus ${minHours} h.`}
                </div>
              </div>
            )}

            {/* (Könttähinnan info jätetään piiloon, ei näytetä €-summaa käyttäjälle) */}
          </div>
        )}

        {/* Viesti */}
        {mode === "message" && (
          <div>
            <label
              htmlFor="contactMessage"
              className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2"
            >
              Viesti tai kysymyksesi
            </label>

            <textarea
              id="contactMessage"
              rows={4}
              placeholder="Kirjoita viestisi…"
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="
                w-full rounded-lg px-3 py-2
                bg-white dark:bg-black/40
                border border-black/10 dark:border-white/10
                text-black/80 dark:text-white/90
                placeholder:text-black/50 dark:placeholder:text-white/50
                caret-black dark:caret-white
                focus:outline-none focus:ring-2 focus:ring-electric/70
                resize-y
              "
            />
          </div>
        )}

        {/* Palaute */}
        {mode === "feedback" && (
          <div className="space-y-3">
            {/* PIN + Tarkista */}
            <div>
              <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
                Palautekoodi (PIN)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}|[0-9]{3}-[0-9]{3}"
                  placeholder="Esim. 482-913"
                  value={feedbackPin}
                  id="feedback_pin"
                  name="feedback_pin"
                  autoComplete="one-time-code"
                  ref={pinInputRef}
                
                 onChange={(e) => {
                    setFeedbackPin(e.target.value);
                    setPinStatus("idle");
                  }}


                  required
                  aria-disabled={pinStatus === "checking"}
                  className="w-full h-10 rounded-lg px-3 bg-white dark:bg-black/40 border border-black/10 dark:border-white/10 text-black/80 dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-electric/70 disabled:opacity-60 disabled:cursor-not-allowed"
                />
                  <ResponsiveButton
                    type="button"
                    onClick={validatePin}
                    disabled={
                      pinStatus === "checking" ||
                      !form.customer_email ||
                      !eventDateIso ||
                      !feedbackPin ||
                      !targetData?.id
                    }
                    title={
                      pinStatus === "checking" ? "Tarkistetaan…"
                      : (!targetData?.id ? "Ladataan esiintyjän tietoja…" 
                        : (!form.customer_email ? "Anna sähköpostisi"
                          : (!eventDateIso ? "Valitse keikkapäivä tai käytä sähköpostilinkkiä"
                            : (!feedbackPin ? "Syötä PIN" : undefined))))
                    }
                    className="px-3 text-sm min-w-[9rem]"
                  >
                    {pinStatus === "checking" ? "Tarkistetaan…" : (pinStatus === "ok" ? "✓ Tarkistettu" : "Tarkista")}
                  </ResponsiveButton>
              </div>

              {/* Keikkapäivä infoteksti */}
              {eventDateIso && (
                <p className="mt-2 text-xs text-black/60 dark:text-white/60">
                  Keikkapäivä: {new Date(eventDateIso + "T12:00:00").toLocaleDateString("fi-FI")}
                </p>
              )}
              <p className="mt-2 text-xs text-black/60 dark:text-white/60">
                Saat PIN-koodin keikan jälkeen lähetettävästä palautekyselystä. Jos avasit tämän sivun sähköpostin
                palautelinkistä, sähköpostiosoite, päivä ja PIN täyttyvät automaattisesti ja lomake avautuu.
                {pinOk ? " Palaute on auki." : " Palaute avautuu esiintymispäivänä."}
              </p>
              {pinStatus !== "idle" && pinStatus !== "checking" && pinStatus !== "ok" && (
                <p className="mt-1 text-xs text-rose-500">
                  {pinStatus === "not_active" ? "Palaute avautuu keikkapäivänä."
                    : pinStatus === "expired" ? "Palautteen aikaikkuna on päättynyt."
                    : "Palautekoodi ei kelpaa. Tarkista koodi tai avaa lomake sähköpostin palautelinkistä."}
                </p>
              )}
            </div>

            {/* Lukittava palaute-osio (teksti + tähdet) */}
            <fieldset
              disabled={feedbackLocked}
              className={feedbackLocked ? "opacity-50 pointer-events-none select-none" : ""}
            >
              <div>
                <label
                  htmlFor="feedbackMessage"
                  className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2"
                >
                  Jätä palautetta:
                </label>
                <textarea
                  id="feedbackMessage"
                  rows={4}
                  placeholder="Kirjoita palautteesi…"
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  className="
                    w-full rounded-lg px-3 py-2
                    bg-white dark:bg-black/40
                    border border-black/10 dark:border-white/10
                    text-black/80 dark:text-white/90
                    placeholder:text-black/50 dark:placeholder:text-white/50
                    caret-black dark:caret-white
                    focus:outline-none focus:ring-2 focus:ring-electric/70
                    resize-y
                    disabled:opacity-60 disabled:cursor-not-allowed
                  "
                />
              </div>

              <div>
                <p className="dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
                  Anna arvosana:
                </p>
                <StarRating rating={stars} onRatingChange={setStars} />
              </div>
            </fieldset>
          </div>
        )}

        {/* Yhteiset kentät */}
        <div>
          <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
            Nimi:
          </label>
          <input
            type="text"
            value={form.customer_name}
            onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
            required
            placeholder="Nimesi…"
            className="
              w-full h-10 rounded-lg px-3
              bg-white dark:bg-black/40
              border border-black/10 dark:border-white/10
              text-black/80 dark:text-white/90
              placeholder:text-black/50 dark:placeholder:text-white/50
              caret-black dark:caret-white
              focus:outline-none focus:ring-2 focus:ring-electric/70
            "
          />
        </div>

        <div>
          <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
            Sähköposti:
          </label>
          <input
            type="email"
            value={form.customer_email}
            onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))}
            required
            placeholder="Sähköpostiosoitteesi…"
            className="
              w-full h-10 rounded-lg px-3
              bg-white dark:bg-black/40
              border border-black/10 dark:border-white/10
              text-black/80 dark:text-white/90
              placeholder:text-black/50 dark:placeholder:text-white/50
              caret-black dark:caret-white
              focus:outline-none focus:ring-2 focus:ring-electric/70
            "
          />
        </div>

        <div>
          <label className="block dark:text-primary/80 text-black/80 font-customFont1 font-semibold mb-2">
            Puhelinnumero:
          </label>
          <input
            type="tel"
            value={form.customer_phone}
            onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
            required={mode !== "feedback"}
            placeholder="Puhelinnumerosi…"
            className="
              w-full h-10 rounded-lg px-3
              bg-white dark:bg-black/40
              border border-black/10 dark:border-white/10
              text-black/80 dark:text-white/90
              placeholder:text-black/50 dark:placeholder:text-white/50
              caret-black dark:caret-white
              focus:outline-none focus:ring-2 focus:ring-electric/70
            "
          />
        </div>

        {/* reCAPTCHA aina näkyvissä */}
        <RecaptchaField
          siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={setRecaptchaValue}
          innerRef={recaptchaRef}
        />

       {/* Submit-nappi – kilpajuoksun kestävä, offline-herkkä ja saavutettava */}
<div className="w-full flex flex-col items-center">
  <ResponsiveButton
    type="submit"
    className="w-full max-w-xs"
    disabled={effectiveDisabled}
    aria-disabled={effectiveDisabled}
    aria-describedby="submit-hint"
    data-state={
      submitting ? "submitting" :
      offline ? "offline" :
      submitDisabled ? "disabled" : "idle"
    }
    title={submitting ? undefined : btnReason}
    onClick={(e) => {
      // Estä tuplat heti (ennen kuin React ehtii päivittää disabledin)
      if (effectiveDisabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (preClickLatch.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      preClickLatch.current = true;
      // Varmuuden vuoksi vapauta lukko hetken päästä
      setTimeout(() => { preClickLatch.current = false; }, 1500);
    }}
  >
    {submitting ? (
      <span className="inline-flex items-center gap-2">
        {/* Kevyt spinneri ilman ulkoisia kirjastoja */}
        <span
          aria-hidden="true"
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
        <span>Lähetetään…</span>
      </span>
    ) : (
      "Lähetä"
    )}
  </ResponsiveButton>

  {/* Vihje + ruudunlukijoiden live-alue */}
  <div
    id="submit-hint"
    role="status"
    aria-live="polite"
    className="mt-2 text-xs text-black/60 dark:text-white/60"
  >
    {!submitting && (btnReason || "\u00A0")}
  </div>
</div>

      </form>
    )}
  </div>

    );
  }

Bookkauslomake.propTypes = {
  bandSlug: PropTypes.string,
  companySlug: PropTypes.string,
  targetType: PropTypes.oneOf(["band", "company"]),
  selectedDate: PropTypes.shape({
    iso: PropTypes.string,
    formatted: PropTypes.string,
  }),
  defaultMode: PropTypes.oneOf(["quote", "message", "feedback"]),
  hideQuoteOption: PropTypes.bool,
  quoteDisabled: PropTypes.bool,
  prefill: PropTypes.shape({
    email: PropTypes.string,
    date:  PropTypes.string,   // YYYY-MM-DD
    pin:   PropTypes.string,   // "######"
    feedback: PropTypes.bool,
  }),
  hideInlineBanner: PropTypes.bool,   // 👈 UUSI
  hideSuccessCard: PropTypes.bool,   // 👈 UUSI
  onSubmitSuccess: PropTypes.func,
  previewStatic: PropTypes.bool,
};
